import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, MapPin, Linkedin, ArrowUpRight } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export function ContactPage() {
  const { currentLanguage } = useLanguage();

  const content = {
    en: {
      title: 'Start a Conversation',
      subtitle: 'Have a project in mind? Let\'s discuss how we can help.',
      email: 'Email',
      address: 'Address',
      linkedin: 'LinkedIn',
      companyName: 'Datex Bilişim AŞ',
      addressLine: 'Çınar Mah. 5003/2 Sokak No: 3-5, İç Kapı No: 508, Bornova, Izmir',
      responseTime: 'We typically respond within 24 hours.'
    },
    tr: {
      title: 'İletişime Geçin',
      subtitle: 'Aklınızda bir proje mi var? Nasıl yardımcı olabileceğimizi konuşalım.',
      email: 'E-posta',
      address: 'Adres',
      linkedin: 'LinkedIn',
      companyName: 'Datex Bilişim AŞ',
      addressLine: 'Çınar Mah. 5003/2 Sokak No: 3-5, İç Kapı No: 508, Bornova, İzmir',
      responseTime: 'Genellikle 24 saat içinde yanıt veriyoruz.'
    },
    de: {
      title: 'Kontakt aufnehmen',
      subtitle: 'Haben Sie ein Projekt im Sinn? Lassen Sie uns besprechen.',
      email: 'E-Mail',
      address: 'Adresse',
      linkedin: 'LinkedIn',
      companyName: 'Datex Bilişim AŞ',
      addressLine: 'Çınar Mah. 5003/2 Sokak No: 3-5, İç Kapı No: 508, Bornova, Izmir',
      responseTime: 'Wir antworten in der Regel innerhalb von 24 Stunden.'
    }
  };

  const t = content[currentLanguage] || content.en;

  return (
    <>
      <SEO page="contact" />
      <div className="min-h-screen pt-28 pb-12 bg-white">
      {/* Subtle background accent */}
      <div 
        className="fixed top-0 right-0 w-full h-full opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 80% 20%, rgba(15, 118, 110, 0.08) 0%, transparent 50%)'
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8">
        {/* Header - Compact */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px" style={{ background: '#0F766E' }} />
            <span 
              className="text-xs uppercase tracking-widest"
              style={{ color: '#0F766E', fontFamily: 'JetBrains Mono, monospace' }}
            >
              Contact
            </span>
          </div>
          <h1 
            className="text-4xl md:text-5xl font-medium tracking-[-0.03em] mb-3"
            style={{ color: 'var(--text-on-light-primary)' }}
            data-testid="contact-page-title"
          >
            {t.title}
          </h1>
          <p className="text-base" style={{ color: 'var(--text-on-light-muted)' }}>
            {t.subtitle}
          </p>
        </div>

        {/* Main Content - Tighter grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left - Form (3 columns) */}
          <div className="lg:col-span-3">
            <div 
              className="rounded-xl p-6 md:p-8"
              style={{ 
                background: '#F8FAFB',
                border: '1px solid rgba(15, 118, 110, 0.1)'
              }}
            >
              <ContactForm compact={true} />
            </div>
          </div>

          {/* Right - Contact Info (2 columns) */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {/* Response time note */}
              <p className="text-xs mb-4" style={{ color: 'var(--text-on-light-subtle)', fontFamily: 'JetBrains Mono, monospace' }}>
                {t.responseTime}
              </p>

              {/* Email Card */}
              <a
                href="mailto:info@datexsystems.com"
                className="group block p-4 rounded-xl transition-all duration-300"
                data-testid="contact-page-email"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.1)'}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ 
                        background: 'rgba(15, 118, 110, 0.1)',
                        border: '1px solid rgba(15, 118, 110, 0.15)'
                      }}
                    >
                      <Mail className="w-4 h-4" style={{ color: '#0F766E' }} />
                    </div>
                    <div>
                      <span 
                        className="text-[10px] uppercase tracking-widest block"
                        style={{ color: 'var(--text-on-light-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {t.email}
                      </span>
                      <p 
                        className="text-sm group-hover:text-[#0F766E] transition-colors duration-300"
                        style={{ color: 'var(--text-on-light-primary)' }}
                      >
                        info@datexsystems.com
                      </p>
                    </div>
                  </div>
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:text-[#0F766E] transition-colors duration-300" style={{ color: 'var(--text-on-light-subtle)' }} />
                </div>
              </a>

              {/* LinkedIn Card */}
              <a
                href="https://linkedin.com/company/datex-systems"
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-4 rounded-xl transition-all duration-300"
                data-testid="contact-page-linkedin"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.1)'}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ 
                        background: 'rgba(15, 118, 110, 0.1)',
                        border: '1px solid rgba(15, 118, 110, 0.15)'
                      }}
                    >
                      <Linkedin className="w-4 h-4" style={{ color: '#0F766E' }} />
                    </div>
                    <div>
                      <span 
                        className="text-[10px] uppercase tracking-widest block"
                        style={{ color: 'var(--text-on-light-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {t.linkedin}
                      </span>
                      <p 
                        className="text-sm group-hover:text-[#0F766E] transition-colors duration-300"
                        style={{ color: 'var(--text-on-light-primary)' }}
                      >
                        /datex-systems
                      </p>
                    </div>
                  </div>
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:text-[#0F766E] transition-colors duration-300" style={{ color: 'var(--text-on-light-subtle)' }} />
                </div>
              </a>

              {/* Address Card */}
              <div 
                className="p-4 rounded-xl"
                data-testid="contact-page-address"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ 
                      background: 'rgba(15, 118, 110, 0.1)',
                      border: '1px solid rgba(15, 118, 110, 0.15)'
                    }}
                  >
                    <MapPin className="w-4 h-4" style={{ color: '#0F766E' }} />
                  </div>
                  <div>
                    <span 
                      className="text-[10px] uppercase tracking-widest block mb-1"
                      style={{ color: 'var(--text-on-light-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {t.address}
                    </span>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-on-light-primary)' }}>{t.companyName}</p>
                    <p className="text-xs leading-relaxed mt-1" style={{ color: 'var(--text-on-light-muted)' }}>
                      {t.addressLine}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
